import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';

import dashboardService from '../../services/dashboard.service';
import { usePreferences } from '../../contexts/PreferencesContext';
import { DASH } from './dashboardTokens';
import { Panel, PanelHead, PanelBody, EmptyState, BlockSkeleton } from './Surface';
import SegmentedToggle from './SegmentedToggle';

/**
 * Zariadenia tímu.
 *
 * Presunuté z nástenky sem: pre zamestnávateľa je to podklad pre podporu
 * („na čom im to beží“), nie denná otázka. Na úvodnej stránke zaberalo miesto
 * pre dianie v úlohách, ktoré sa mení každý deň.
 */
const PLATFORM_COLORS = {
    ios: '#007AFF',
    android: '#3DDC84',
    macos: '#8E8E93',
    windows: '#00A4EF',
    linux: '#FCC624',
    web: '#64748b',
    unknown: '#94a3b8',
};

const DEVICE_CHART_VIEWS = [
    { value: 'platform', labelKey: 'platformsTab' },
    { value: 'appVersion', labelKey: 'appVersionsTab' },
];

const MOBILE_PLATFORMS = new Set(['ios', 'android']);

const DEVICE_PLATFORM_SCOPES = [
    { value: 'mobile', labelKey: 'mobilePlatforms' },
    { value: 'all', labelKey: 'allPlatforms' },
];

const DeviceInsights = () => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartView, setChartView] = useState('platform');
    const [platformScope, setPlatformScope] = useState('mobile');

    useEffect(() => {
        let cancelled = false;
        dashboardService.getStats(dayjs().format('YYYY-MM-DD'))
            .then((res) => { if (!cancelled) setStats(res.data); })
            .catch((err) => console.error('Error fetching device stats', err))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const deviceStats = useMemo(() => stats?.deviceStats || {
        employeesWithDevices: 0,
        byPlatform: [],
        byAppVersion: [],
    }, [stats?.deviceStats]);

    const formatPlatformLabel = useCallback((platform) => {
        const key = `pages.dashboard.devices.platforms.${platform}`;
        const translated = t(key);
        return translated !== key ? translated : platform;
    }, [t]);

    const chartViewOptions = useMemo(
        () => DEVICE_CHART_VIEWS.map(({ value, labelKey }) => ({
            value,
            label: t(`pages.dashboard.devices.${labelKey}`),
        })),
        [t]
    );

    const platformScopeOptions = useMemo(
        () => DEVICE_PLATFORM_SCOPES.map(({ value, labelKey }) => ({
            value,
            label: t(`pages.dashboard.devices.${labelKey}`),
        })),
        [t]
    );

    const chartData = useMemo(() => {
        if (chartView === 'platform') {
            return (deviceStats.byPlatform || [])
                .filter((row) => platformScope === 'all' || MOBILE_PLATFORMS.has(row.platform))
                .map((row) => ({
                    label: formatPlatformLabel(row.platform),
                    count: row.count,
                    color: PLATFORM_COLORS[row.platform] || PLATFORM_COLORS.unknown,
                }));
        }
        return (deviceStats.byAppVersion || []).map((row) => ({
            label: row.version,
            count: row.count,
            color: theme.palette.primary.main,
        }));
    }, [chartView, platformScope, deviceStats, formatPlatformLabel, theme.palette.primary.main]);

    const hasData = chartData.length > 0;

    const chartOptions = useMemo(() => {
        const isDark = theme.palette.mode === 'dark';
        const textColor = isDark ? '#94a3b8' : '#64748B';
        const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)';

        return {
            chart: { toolbar: { show: false }, fontFamily: 'inherit', animations: { speed: 400 } },
            plotOptions: {
                bar: {
                    horizontal: true,
                    borderRadius: 6,
                    borderRadiusApplication: 'end',
                    barHeight: '52%',
                    distributed: true,
                },
            },
            dataLabels: {
                enabled: true,
                style: { fontSize: '12px', fontWeight: 700 },
                offsetX: 24,
                formatter: (val) => (val > 0 ? String(val) : ''),
            },
            legend: { show: false },
            xaxis: {
                categories: chartData.map((d) => d.label),
                labels: { style: { colors: textColor, fontSize: '12px' } },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: { labels: { style: { colors: textColor, fontSize: '12px' } } },
            grid: {
                borderColor: gridColor,
                strokeDashArray: 0,
                yaxis: { lines: { show: false } },
                padding: { left: 8, right: 24 },
            },
            colors: chartData.map((d) => d.color),
            tooltip: { theme: isDark ? 'dark' : 'light', x: { formatter: (val) => `${val}` } },
        };
    }, [theme, chartData]);

    const chartSeries = useMemo(() => ([{
        name: chartView === 'platform'
            ? t('pages.dashboard.devices.employeesLabel')
            : t('pages.dashboard.devices.appVersionsLabel'),
        data: chartData.map((d) => d.count),
    }]), [chartView, chartData, t]);

    const summaryLabel = useMemo(() => (
        t('pages.dashboard.devices.summary')
            .replace('{employees}', String(deviceStats.employeesWithDevices || 0))
    ), [deviceStats.employeesWithDevices, t]);

    return (
        <Panel>
            <PanelHead
                title={t('pages.dashboard.devices.title')}
                meta={summaryLabel}
                actions={(
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                        <SegmentedToggle
                            value={chartView}
                            onChange={setChartView}
                            options={chartViewOptions}
                            size="sm"
                        />
                        {chartView === 'platform' && (
                            <SegmentedToggle
                                value={platformScope}
                                onChange={setPlatformScope}
                                options={platformScopeOptions}
                                size="sm"
                            />
                        )}
                    </Stack>
                )}
            />
            <PanelBody>
                {loading ? (
                    <BlockSkeleton height={DASH.chartHeight} />
                ) : !hasData ? (
                    <EmptyState
                        title={t('pages.dashboard.devices.noData')}
                        hint={t('pages.dashboard.devices.noDataHint')}
                    />
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Chart
                            key={chartView}
                            options={chartOptions}
                            series={chartSeries}
                            type="bar"
                            height={DASH.chartHeight}
                        />
                    </Box>
                )}
            </PanelBody>
        </Panel>
    );
};

export default DeviceInsights;
